
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import AMapLoader from '@amap/amap-jsapi-loader'
import { MapSearchRadius } from '@/utils/coinfig'
import { getCurrentPosition } from '@/utils/coord'

// 声明高德地图全局变量
declare global {
  interface Window {
    AMap: any
  }
}

// 定义位置信息接口
export interface LocationInfo {
  position: {
    longitude: number
    latitude: number
  }
  address: string
}

@Component({
  name: 'LocationAutoDetect',
})
export default class LocationAutoDetect extends Vue {
  // 是否激活位置获取
  @Prop({ default: false }) private value!: boolean
  // 初始位置信息
  @Prop() private location?: LocationInfo

  // 组件内部状态
  private active = false
  // 是否正在定位
  private locating = false
  // 地理编码服务
  private geocoder: any = null
  // 定位服务
  private geolocation: any = null
  // 高德地图实例（仅用于API调用，不显示地图）
  private map: any = null

  @Watch('value')
  onValueChange(val: boolean) {
    this.active = val
    if (val) {
      this.initAMapServices()
    }
  }

  @Watch('active')
  onActiveChange(val: boolean) {
    this.$emit('input', val)
    if (!val) {
      this.destroyServices()
    }
  }

  mounted() {
    this.active = this.value
    if (this.active) {
      this.initAMapServices()
    }
  }

  beforeDestroy() {
    this.destroyServices()
  }

  /**
   * 初始化高德地图服务
   */
  private initAMapServices() {
    // 如果已经初始化，不再重复初始化
    if (this.geolocation && this.geocoder) {
      this.locateCurrentPosition()
      return
    }


    // 加载高德地图
    AMapLoader.load({
      key: process.env.VUE_APP_AMAP_KEY || "", // Web端开发者Key
      version: "2.0", // JSAPI版本
      plugins: ["AMap.Geocoder", "AMap.Geolocation"], // 需要使用的插件
    }).then((AMap) => {
      console.log("高德地图服务加载成功")

      // 创建一个隐藏的地图实例（仅用于API调用）
      this.map = new AMap.Map("location-auto-detect-container", {
        viewMode: '2D',
        zoom: 15,
        center: [116.397428, 39.90923], // 默认北京
        visible: false // 不可见
      })

      // 初始化地理编码服务
      this.geocoder = new AMap.Geocoder({
        radius: MapSearchRadius, // 查找范围
        extensions: 'all' // 返回基本+附加地址信息
      })

      // 初始化定位服务
      this.geolocation = new AMap.Geolocation({
        enableHighAccuracy: true, // 是否使用高精度定位
        timeout: 10000, // 超时时间
        zoomToAccuracy: false, // 不调整地图视野
        showButton: false, // 不显示定位按钮
        panToLocation: false, // 不移动到定位位置
        maximumAge: 0 // 不缓存定位结果
      })

      // 地图加载完成后，立即获取当前位置
      this.locateCurrentPosition()

    }).catch(e => {
      console.error('高德地图服务加载失败', e)
      this.$message.error('位置服务加载失败')
      this.active = false
    })
  }

  /**
   * 定位到当前位置
   */
  private locateCurrentPosition() {
    if (!this.geolocation || this.locating) return

    this.locating = true
    this.$emit('locating', true)


    let successCallback = (result: any) => {
      this.locating = false
      this.$emit('locating', false)
      const position: [number, number] = [result.position.lng, result.position.lat]

      // 获取地址信息
      this.getLocationName(position).then(address => {
        const locationInfo: LocationInfo = {
          position: {
            longitude: position[0],
            latitude: position[1]
          },
          address
        }

        // 发送位置信息
        this.$emit('location-change', locationInfo)
      })
    }

    let errorCallback = (result: any) => {
      this.locating = false
      this.$emit('location-error', result.message)
      this.$message.error('获取位置失败：' + result.message)
    }

    getCurrentPosition(
      this, this.geolocation, successCallback, errorCallback)


    // this.geolocation.getCurrentPosition((status: string, result: any) => {
    //   this.locating = false
    //   this.$emit('locating', false)

    //   if (status === 'complete') {
    //     const position: [number, number] = [result.position.lng, result.position.lat]

    //     // 获取地址信息
    //     this.getLocationName(position).then(address => {
    //       const locationInfo: LocationInfo = {
    //         position: {
    //           longitude: position[0],
    //           latitude: position[1]
    //         },
    //         address
    //       }

    //       // 发送位置信息
    //       this.$emit('location-change', locationInfo)
    //     })
    //   } else {
    //     // 定位失败
    //     this.$emit('location-error', result.message)
    //     this.$message.error('获取位置失败：' + result.message)
    //   }
    // })
  }

  /**
   * 获取位置的地点名称
   */
  private getLocationName(position: [number, number]): Promise<string> {
    return new Promise((resolve) => {
      if (!this.geocoder) {
        resolve('未知地点')
        return
      }

      this.geocoder.getAddress(position, (status: string, result: any) => {
        if (status === 'complete') {
          // 获取成功，返回地址信息
          const address = result.regeocode.formattedAddress
          resolve(address)
        } else {
          // 获取失败，返回未知地点
          resolve('未知地点')
        }
      })
    })
  }

  /**
   * 手动触发位置获取
   */
  public detectLocation() {
    if (this.active) {
      this.locateCurrentPosition()
    } else {
      this.active = true
    }
  }

  /**
   * 销毁地图服务
   */
  private destroyServices() {
    if (this.map) {
      this.map.destroy()
      this.map = null
    }
    this.geolocation = null
    this.geocoder = null
  }
}
